import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { ArrowClockwise, Trash } from 'react-bootstrap-icons';
import apiClient, { API_ENDPOINTS } from '../api/config';
import { formatLocalDateTimeFromUTC, getUserTimeZoneLabel } from '../utils/datetime';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import TestDatasetPreviewModal from '../components/testdata/TestDatasetPreviewModal';
import CustomDropdown, { CustomDropdownOption } from '../components/CustomDropdown';
import StreamingJobLogsModal from '../components/logs/StreamingJobLogsModal';
import { jobTypeLabel, normalizeJobType } from '../utils/jobType';
import { projectExecutionsRoute, projectTestCasesRoute, projectTabRoute } from '../utils/routes';
import { filenameFromContentDisposition, triggerDownloadFromBlob } from '../utils/download';
import './TestGenerations.css';

type TGItem = {
  id: number;
  repository_id?: number | null;
  repository_name?: string | null;
  project_id?: number | null;
  project_name?: string | null;
  organization_id?: number | null;
  generation_kind?: string;
  job_type?: string;
  status: string;
  progress: number;
  artifact_counts?: Record<string, number> | null;
  document_count?: number;
  created_at: string;
  completed_at?: string | null;
  output_manifest?: string | null;
};

type TGSummary = {
  total_jobs: number;
  completed: number;
  failed: number;
  cancelled: number;
  running: number;
  pending: number;
};

type PagedTGResponse = {
  items: TGItem[];
  total: number;
  page: number;
  page_size: number;
  summary: TGSummary;
};

const PAGE_SIZE = 20;

const STATUS_OPTIONS: CustomDropdownOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'pending', label: 'Pending' },
  { value: 'retrying', label: 'Retrying' },
];

const KIND_OPTIONS: CustomDropdownOption[] = [
  { value: '', label: 'All Types' },
  { value: 'testcases', label: 'Test Cases' },
  { value: 'playwright', label: 'Playwright' },
  { value: 'gherkin', label: 'Gherkin' },
  { value: 'synthetic_data', label: 'Synthetic Data' },
];

export interface TestGenerationsProps {
  projectId: number;
  projectName?: string;
  initialJobId?: number | null;
}

function getStatusKey(status?: string): string {
  return (status || 'unknown').toLowerCase();
}

function getRequirementCount(item: TGItem): number {
  return Number(item.artifact_counts?.requirements ?? item.document_count ?? 0);
}

function getTestCaseCount(item: TGItem): number {
  return Number(item.artifact_counts?.test_cases ?? 0);
}

const TestGenerations: React.FC<TestGenerationsProps> = ({ projectId, projectName, initialJobId = null }) => {
  const [items, setItems] = useState<TGItem[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<TGSummary>({ total_jobs: 0, completed: 0, failed: 0, cancelled: 0, running: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [logModal, setLogModal] = useState<{
    show: boolean;
    title: string;
    jobId?: number;
    status?: string;
  }>({ show: false, title: '' });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [previewDataset, setPreviewDataset] = useState<any | null>(null);
  const [loadingDataset] = useState(false);

  const canViewLogs = user?.can_view_logs ?? true;

  // Server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Filter inputs (what user types) vs applied (committed to API)
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [kindInput, setKindInput] = useState('');
  const [appliedKind, setAppliedKind] = useState('');
  const [fromDateInput, setFromDateInput] = useState('');
  const [appliedFromDate, setAppliedFromDate] = useState('');
  const [toDateInput, setToDateInput] = useState('');
  const [appliedToDate, setAppliedToDate] = useState('');
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [projectId]);

  // Track applied filters in a ref so the polling interval always uses latest values
  const appliedFiltersRef = useRef({ appliedSearch, appliedStatus, appliedKind, appliedFromDate, appliedToDate, currentPage });
  useEffect(() => {
    appliedFiltersRef.current = { appliedSearch, appliedStatus, appliedKind, appliedFromDate, appliedToDate, currentPage };
  }, [appliedSearch, appliedStatus, appliedKind, appliedFromDate, appliedToDate, currentPage]);

  const buildFetchParams = useCallback((overrides?: { page?: number; search?: string; status?: string; kind?: string }) => {
    const { appliedSearch: s, appliedStatus: st, appliedKind: k, appliedFromDate: fd, appliedToDate: td, currentPage: p } = appliedFiltersRef.current;
    const params: Record<string, string | number> = {
      page: overrides?.page ?? p,
      page_size: PAGE_SIZE,
      project_id: projectId,
    };
    const search = overrides?.search !== undefined ? overrides.search : s;
    const status = overrides?.status !== undefined ? overrides.status : st;
    const kind = overrides?.kind !== undefined ? overrides.kind : k;
    if (search) params.search = search;
    if (status) params.status = status;
    if (kind) params.generation_kind = kind;
    if (fd) params.date_from = fd;
    if (td) params.date_to = td;
    return params;
  }, [projectId]);

  const fetchItems = useCallback(async (options?: { silent?: boolean; params?: Record<string, string | number> }) => {
    try {
      if (!options?.silent) {
        setLoading(true);
      }
      if (!options?.silent) {
        setError(null);
      }
      const params = options?.params ?? buildFetchParams();
      const res = await apiClient.get<PagedTGResponse>(API_ENDPOINTS.TEST_GENERATIONS, { params });
      const data = res.data;
      setItems(data.items || []);
      setTotal(data.total || 0);
      if (data.summary) {
        setSummary(data.summary);
      }
      setError(null);
    } catch (e: any) {
      console.error(e);
      if (!options?.silent) {
        setError(e?.response?.data?.detail || 'Failed to load test generations');
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [buildFetchParams]);

  // Re-fetch when page or applied filters change
  useEffect(() => {
    fetchItems({ params: buildFetchParams() }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentPage, appliedSearch, appliedStatus, appliedKind, appliedFromDate, appliedToDate]);

  // Auto-refresh while this dashboard is open so queued jobs move to running/completed without navigation.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchItems({ silent: true, params: buildFetchParams() }).catch(() => undefined);
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchItems, buildFetchParams]);

  // Page numbers for pagination display
  const pageNumbers = useMemo(() => {
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    const start = Math.max(1, Math.min(currentPage - half, totalPages - windowSize + 1));
    const end = Math.min(totalPages, start + windowSize - 1);
    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  }, [currentPage, totalPages]);

  const pageRangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageRangeEnd = Math.min(currentPage * PAGE_SIZE, total);

  // --- Filter handlers ---
  function applyFilters(): void {
    if ((fromDateInput && !toDateInput) || (!fromDateInput && toDateInput)) {
      setDateError('Please select both a start date and an end date.');
      return;
    }
    setDateError('');
    setAppliedSearch(searchInput);
    setAppliedStatus(statusInput);
    setAppliedKind(kindInput);
    setAppliedFromDate(fromDateInput);
    setAppliedToDate(toDateInput);
    setCurrentPage(1);
    setSelectedIds([]);
  }

  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') { applyFilters(); }
  }

  function handleClearFilters(): void {
    setSearchInput('');
    setStatusInput('');
    setKindInput('');
    setFromDateInput('');
    setToDateInput('');
    setAppliedSearch('');
    setAppliedStatus('');
    setAppliedKind('');
    setAppliedFromDate('');
    setAppliedToDate('');
    setDateError('');
    setCurrentPage(1);
    setSelectedIds([]);
  }

  const hasActiveFilters = !!(appliedSearch || appliedStatus || appliedKind || appliedFromDate || appliedToDate);

  // --- Action handlers ---
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this job? This cannot be undone.')) return;
    try {
      await apiClient.delete(API_ENDPOINTS.DELETE_TEST_GENERATION(id));
      fetchItems({ params: buildFetchParams() }).catch(() => undefined);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.detail || 'Failed to delete test generation');
    }
  };

  const handleCancel = async (id: number) => {
    try {
      await apiClient.post(API_ENDPOINTS.CANCEL_TEST_GENERATION(id));
      setItems(prev => prev.map(i => (i.id === id ? { ...i, status: 'cancelled' } : i)));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to cancel job');
    }
  };

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      const pageIds = items.map((i) => i.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
      return;
    }
    const pageIds = new Set(items.map((i) => i.id));
    setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
  };

  const toggleSelectOne = (id: number, checked: boolean) => {
    setSelectedIds(prev => checked ? Array.from(new Set([...prev, id])) : prev.filter(x => x !== id));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected jobs? This cannot be undone.`)) return;
    setBulkRunning(true);
    try {
      for (const id of selectedIds) {
        try {
          await apiClient.delete(API_ENDPOINTS.DELETE_TEST_GENERATION(id));
        } catch (e) {
          // continue
        }
      }
      setSelectedIds([]);
      await fetchItems({ params: buildFetchParams() });
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const handleViewLogs = async (item: TGItem, displayName: string) => {
    setLogModal({
      show: true,
      title: `${displayName} - workflow.log (Job #${item.id})`,
      jobId: item.id,
      status: item.status,
    });
  };

  if (loading && items.length === 0) {
    return (
      <div className="jb-loading">
        <Spinner animation="border" size="sm" />
        <span>Loading jobs…</span>
      </div>
    );
  }

  return (
    <div className="jb-page jb-page--embedded">
      {/* ---- Page header ---- */}
      <div className="jb-header">
        <div className="jb-header-left">
          <h1 className="jb-title">Jobs</h1>
          <p className="jb-subtitle">
            {projectName ? `All jobs for ${projectName}` : 'All jobs for this project'}
          </p>
        </div>
      </div>

      {/* ---- Error alert ---- */}
      {error && (
        <Alert variant="danger" className="jb-alert" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ---- Summary stats ---- */}
      <div className="jb-stats-grid">
        <div className="jb-stat-card">
          <span className="jb-stat-label">Total Jobs</span>
          <strong className="jb-stat-value">{summary.total_jobs}</strong>
        </div>
        <div className="jb-stat-card jb-stat-card--completed">
          <span className="jb-stat-label">Completed</span>
          <strong className="jb-stat-value">{summary.completed}</strong>
        </div>
        <div className="jb-stat-card jb-stat-card--failed">
          <span className="jb-stat-label">Failed</span>
          <strong className="jb-stat-value">{summary.failed}</strong>
        </div>
        <div className="jb-stat-card jb-stat-card--cancelled">
          <span className="jb-stat-label">Cancelled</span>
          <strong className="jb-stat-value">{summary.cancelled}</strong>
        </div>
        <div className="jb-stat-card">
          <span className="jb-stat-label">Running</span>
          <strong className="jb-stat-value">{summary.running}</strong>
        </div>
        <div className="jb-stat-card">
          <span className="jb-stat-label">Pending</span>
          <strong className="jb-stat-value">{summary.pending}</strong>
        </div>
      </div>

      {/* ---- Filter bar ---- */}
      <div className="jb-filter-row">
        {/* Search */}
        <div className="jb-filter-search">
          <svg className="jb-filter-search-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8" />
            <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            className="jb-filter-input"
            type="text"
            placeholder="Search by repository name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>

        {/* Status filter */}
        <div className="jb-filter-dropdown">
          <CustomDropdown
            value={statusInput}
            options={STATUS_OPTIONS}
            onChange={(v) => setStatusInput(v)}
            placeholder="All Statuses"
          />
        </div>

        {/* Job type filter */}
        <div className="jb-filter-dropdown">
          <CustomDropdown
            value={kindInput}
            options={KIND_OPTIONS}
            onChange={(v) => setKindInput(v)}
            placeholder="All Types"
          />
        </div>

        {/* Date range filter */}
        <div className="jb-filter-date filter-date-ph-wrap">
          <input
            className={`jb-filter-date-input${!fromDateInput ? ' date-empty' : ''}${dateError ? ' jb-filter-date-input--error' : ''}`}
            type="date"
            aria-label="From date"
            value={fromDateInput}
            onChange={(e) => { setFromDateInput(e.target.value); setDateError(''); }}
          />
          {!fromDateInput && <span className="filter-date-ph" aria-hidden="true">Start Date</span>}
        </div>
        <div className="jb-filter-date filter-date-ph-wrap">
          <input
            className={`jb-filter-date-input${!toDateInput ? ' date-empty' : ''}${dateError ? ' jb-filter-date-input--error' : ''}`}
            type="date"
            aria-label="To date"
            value={toDateInput}
            onChange={(e) => { setToDateInput(e.target.value); setDateError(''); }}
          />
          {!toDateInput && <span className="filter-date-ph" aria-hidden="true">End Date</span>}
        </div>
        {dateError && <span className="jb-filter-date-error">{dateError}</span>}

        <button className="jb-btn-search" onClick={applyFilters} type="button" disabled={loading}>
          Search
        </button>

        {hasActiveFilters && (
          <button className="jb-btn-clear" onClick={handleClearFilters} type="button">
            Clear
          </button>
        )}
      </div>

      {/* ---- Toolbar ---- */}
      <div className="jb-toolbar-row">
        <span className="jb-meta-note">Times shown in {getUserTimeZoneLabel()}</span>
        <span className="jb-jobs-found">{total} {total === 1 ? 'job' : 'jobs'} found</span>
        <div className="jb-toolbar">
          {selectedIds.length > 0 && (
            <button
              type="button"
              className="jb-btn jb-btn--bulk-delete"
              disabled={bulkRunning}
              onClick={handleBulkDelete}
            >
              {bulkRunning ? 'Deleting…' : `Delete Selected (${selectedIds.length})`}
            </button>
          )}
          <button
            type="button"
            className="jb-btn jb-btn--outline"
            onClick={() => fetchItems({ params: buildFetchParams() })}
            disabled={loading}
          >
            <ArrowClockwise />
            Refresh
          </button>
        </div>
      </div>

      {/* ---- Main table card ---- */}
      <div className="jb-card">
        <div className="jb-table-wrapper">
          {loading ? (
            <div className="jb-empty-state">
              <Spinner animation="border" size="sm" />
              <p className="jb-empty-text">Loading…</p>
            </div>
          ) : items.length === 0 ? (
            <div className="jb-empty-state">
              <div className="jb-empty-icon">📋</div>
              <p className="jb-empty-text">No jobs found.</p>
            </div>
          ) : (
            <>
            <label className="jb-mobile-select-all">
              <input
                type="checkbox"
                className="form-check-input"
                aria-label="select all jobs on this page"
                checked={items.length > 0 && items.every((item) => selectedIds.includes(item.id))}
                onChange={(e) => toggleSelectAll(e.currentTarget.checked)}
              />
              Select all jobs on this page
            </label>
            <table className="jb-table" data-responsive-cards="true">
              <thead>
                <tr>
                  <th className="jb-td-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      aria-label="select all jobs"
                      checked={items.length > 0 && items.every((item) => selectedIds.includes(item.id))}
                      onChange={(e) => toggleSelectAll(e.currentTarget.checked)}
                    />
                  </th>
                  <th>Job ID</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th className="jb-th-center">Requirements</th>
                  <th className="jb-th-center">Test Cases</th>
                  <th>Started</th>
                  <th>Completed</th>
                  <th>Progress</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const jobType = normalizeJobType(item.job_type || item.generation_kind);
                  const targetProjectId = item.project_id || projectId;
                  const displayName = item.repository_name || item.project_name || projectName || `Job #${item.id}`;
                  const canOpenResults = Boolean(targetProjectId);
                  const requirementCount = getRequirementCount(item);
                  const testCaseCount = getTestCaseCount(item);
                  const statusKey = getStatusKey(item.status);
                  const progress = item.progress ?? 0;

                  return (
                    <tr key={item.id} className={initialJobId === item.id ? 'jb-row--highlighted' : undefined}>
                      {/* Checkbox */}
                      <td className="jb-td-check" data-label="Select">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          aria-label={`select job ${item.id}`}
                          checked={selectedIds.includes(item.id)}
                          onChange={(e) => toggleSelectOne(item.id, e.currentTarget.checked)}
                        />
                      </td>

                      {/* ID */}
                      <td data-label="Job ID">
                        <span className="jb-job-id">#{item.id}</span>
                      </td>

                      {/* Type */}
                      <td data-label="Type">
                        <span className={`jb-type-badge jb-type-badge--${jobType === 'testcases' ? 'requirements' : jobType === 'synthetic_data' ? 'synthetic' : jobType}`}>
                          {jobTypeLabel(jobType)}
                        </span>
                      </td>

                      {/* Status */}
                      <td data-label="Status">
                        <span className={`jb-status-badge jb-status-badge--${statusKey}`}>
                          <span className="jb-status-dot" />
                          {item.status}
                        </span>
                      </td>

                      {/* Requirements */}
                      <td className="jb-td-center" data-label="Requirements">
                        <span className="jb-count-value">{requirementCount || '—'}</span>
                      </td>

                      {/* Test cases */}
                      <td className="jb-td-center" data-label="Test Cases">
                        <span className="jb-count-value jb-count-value--accent">{testCaseCount || '—'}</span>
                      </td>

                      {/* Started */}
                      <td data-label="Started">
                        <span className="jb-datetime">{formatLocalDateTimeFromUTC(item.created_at)}</span>
                      </td>

                      {/* Completed */}
                      <td data-label="Completed">
                        <span className="jb-datetime">
                          {item.completed_at ? formatLocalDateTimeFromUTC(item.completed_at) : '—'}
                        </span>
                      </td>

                      {/* Progress */}
                      <td data-label="Progress">
                        <div className="jb-progress-wrap">
                          <div className="jb-progress-bar-track">
                            <div
                              className={`jb-progress-bar-fill jb-progress-bar-fill--${statusKey}`}
                              data-progress={Math.min(100, Math.max(0, progress))}
                              ref={(el) => { if (el) el.style.width = `${Math.min(100, Math.max(0, progress))}%`; }}
                            />
                          </div>
                          <span className="jb-progress-label">{progress}%</span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td data-label="Actions" data-card-actions="true">
                        <div className="jb-row-actions">
                          {canViewLogs && (
                            <button
                              type="button"
                              className="jb-btn jb-btn--ghost"
                              onClick={() => handleViewLogs(item, displayName)}
                            >
                              Logs
                            </button>
                          )}
                          {item.status?.toLowerCase() === 'running' && (
                            <button
                              type="button"
                              className="jb-btn jb-btn--warning"
                              onClick={() => handleCancel(item.id)}
                            >
                              Stop
                            </button>
                          )}
                          {canOpenResults && (
                            <button
                              type="button"
                              className="jb-btn jb-btn--ghost"
                              onClick={() => {
                                if (jobType === 'testcases') {
                                  navigate(projectTestCasesRoute(targetProjectId, { jobId: item.id }));
                                } else if (jobType === 'playwright') {
                                  navigate(projectExecutionsRoute(targetProjectId, { jobId: item.id }));
                                } else if (jobType === 'gherkin') {
                                  navigate(projectTabRoute(targetProjectId, 'gherkin', { gherkinJobId: item.id, trackGherkin: 1 }));
                                } else if (jobType === 'synthetic_data') {
                                  navigate(`/projects/${targetProjectId}/test-data`);
                                }
                              }}
                            >
                              Results
                            </button>
                          )}
                          <button
                            type="button"
                            className="jb-btn jb-btn--icon jb-btn--danger"
                            disabled={item.status?.toLowerCase() === 'running'}
                            onClick={() => handleDelete(item.id)}
                            title="Delete job"
                            aria-label={`Delete job ${item.id}`}
                          >
                            <Trash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </>
          )}
        </div>

        {/* ---- Pagination ---- */}
        {!loading && total > 0 && (
          <div className="jb-card-footer">
            <span className="jb-pagination-summary">
              Showing {pageRangeStart}–{pageRangeEnd} of {total} jobs
            </span>
            <div className="jb-pagination-controls">
              {/* First */}
              <button
                type="button"
                className="jb-page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                ← First
              </button>

              {/* Prev */}
              <button
                type="button"
                className="jb-page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Prev
              </button>

              {/* Page numbers */}
              {pageNumbers.map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  className={`jb-page-btn${currentPage === pageNum ? ' jb-page-btn--active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              ))}

              {/* Next */}
              <button
                type="button"
                className="jb-page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              >
                Next
              </button>

              {/* Last */}
              <button
                type="button"
                className="jb-page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                Last →
              </button>
            </div>
          </div>
        )}
      </div>

      <StreamingJobLogsModal
        show={logModal.show}
        title={logModal.title}
        jobId={logModal.jobId}
        initialStatus={logModal.status}
        downloadName="workflow.log"
        onHide={() => setLogModal({ show: false, title: '' })}
      />

      {/* ---- Synthetic Data Preview Modal ---- */}
      {previewDataset && (
        <TestDatasetPreviewModal
          dataset={previewDataset}
          defaultView="json"
          onClose={() => setPreviewDataset(null)}
          onDownload={async (datasetId) => {
            const response = await apiClient.get(API_ENDPOINTS.TEST_DATA_DOWNLOAD(previewDataset.project_id, datasetId), { responseType: 'blob' });
            triggerDownloadFromBlob(
              response.data,
              filenameFromContentDisposition(response.headers?.['content-disposition'], 'synthetic_data.csv'),
            );
          }}
        />
      )}
      {loadingDataset && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <Spinner animation="border" variant="light" />
        </div>
      )}
    </div>
  );
};

export default TestGenerations;
